/** Yandex Maps API key — карточка в Настройки → Общие.
 *
 * Раньше ключ читался только из NEXT_PUBLIC_YANDEX_MAPS_API_KEY на этапе
 * сборки витрины (Vercel/VPS env) — владелец магазина не имеет туда
 * доступа и смена ключа требовала полного ребилда. Теперь хранится в БД
 * (app_settings.yandex_maps_api_key, миграция 051) и отдаётся публично
 * через GET /api/v1/site-config — в отличие от Google Translate это не
 * серверный секрет, ключ и так виден в браузере (JS SDK карты).
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, Save, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

export function YandexMapsSettings({ keySet }: { keySet: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const [value, setValue] = useState("");

  const saveMutation = useMutation({
    mutationFn: async (yandex_maps_api_key: string) => {
      await api.patch("/settings", { yandex_maps_api_key });
    },
    onSuccess: () => {
      toast.success(value.trim() ? "Ключ сохранён" : "Ключ удалён");
      setValue("");
      queryClient.invalidateQueries({ queryKey: ["admin-settings"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? "Ошибка сохранения"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Yandex Maps API
          {keySet ? (
            <Badge variant="secondary" className="gap-1 text-[10px]">
              <CheckCircle2 className="h-3 w-3" /> ключ задан
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1 text-[10px] text-destructive">
              <XCircle className="h-3 w-3" /> не настроен
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Используется для карты на странице «Магазины» на витрине. Ключ
          получается на{" "}
          <a
            href="https://developer.tech.yandex.ru/services/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            developer.tech.yandex.ru
          </a>{" "}
          (раздел JavaScript API и MapKit). Применяется сразу, без
          пересборки сайта.
        </p>
        <div className="space-y-1.5">
          <Label>{keySet ? "Заменить ключ" : "API-ключ"}</Label>
          <Input
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={keySet ? "оставьте пустым, чтобы не менять" : "введите ключ"}
            className="font-mono text-xs max-w-md"
          />
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={!value.trim() || saveMutation.isPending}
            onClick={() => saveMutation.mutate(value.trim())}
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1 h-4 w-4" />
            )}
            Сохранить ключ
          </Button>
          {keySet && (
            <Button
              size="sm"
              variant="outline"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate("")}
            >
              Удалить ключ
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
